import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackPageView } from '../utils/gtm';

/**
 * Custom hook for tracking React Router navigation with GTM Data Layer
 * Automatically tracks page views when the route changes in SPA
 * 
 * @param {boolean} enabled - Whether tracking is enabled (default: true)
 * @param {function} getPageTitle - Function to get page title from location (optional)
 * @param {function} getAdditionalData - Function to get additional tracking data (optional)
 */
export const useRouterTracking = (
  enabled = true, 
  getPageTitle = null, 
  getAdditionalData = null
) => {
  const location = useLocation();

  useEffect(() => {
    if (!enabled) return;

    // Small delay to ensure page has loaded and title has been updated
    const trackingTimer = setTimeout(() => {
      const pagePath = location.pathname + location.search;
      const pageTitle = getPageTitle ? getPageTitle(location) : document.title;
      const additionalData = getAdditionalData ? getAdditionalData(location) : {};

      // Track the page view with GTM Data Layer
      trackPageView(pagePath, pageTitle, {
        page_hash: location.hash,
        page_search: location.search,
        page_state: location.state,
        ...additionalData
      });

      // Log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('🛣️ Router Navigation Tracked:', {
          pagePath,
          pageTitle,
          location: location
        });
      }
    }, 100); // Small delay to ensure DOM updates

    return () => clearTimeout(trackingTimer);
  }, [location, enabled, getPageTitle, getAdditionalData]);

  return location;
};

/**
 * Helper function to get page title based on route path
 * @param {Object} location - React Router location object
 * @returns {string} - Page title
 */
export const getPageTitleFromRoute = (location) => {
  const path = location.pathname;
  
  const routeTitles = {
    '/': 'Home - Twisted Cloud Solutions',
    '/services/ai-integration': 'AI Integration Services - Twisted Cloud Solutions',
    '/services/intelligent-business-networks': 'Intelligent Business Networks - Twisted Cloud Solutions',
    '/services/mobile-applications': 'Mobile Applications - Twisted Cloud Solutions',
    '/case-studies': 'Case Studies - Twisted Cloud Solutions',
    '/case-studies/ferments': 'Ferments Case Study - Twisted Cloud Solutions',
    '/ferments-demo': 'Ferments Demo - Twisted Cloud Solutions',
    '/about': 'About Us - Twisted Cloud Solutions',
    '/contact': 'Contact - Twisted Cloud Solutions'
  };

  // Check for exact match first
  if (routeTitles[path]) {
    return routeTitles[path];
  }

  // Check for case study detail pages
  if (path.startsWith('/case-studies/') && path !== '/case-studies') {
    return `Case Study - Twisted Cloud Solutions`;
  }

  // Check for service pages
  if (path.startsWith('/services/')) {
    return `Services - Twisted Cloud Solutions`;
  }

  // Fall back to document title or default
  return document.title || 'Twisted Cloud Solutions';
};

/**
 * Helper function to get additional tracking data based on route
 * @param {Object} location - React Router location object
 * @returns {Object} - Additional tracking data
 */
export const getAdditionalTrackingData = (location) => {
  const path = location.pathname;
  const data = {
    page_category: 'general'
  };

  // Categorize pages for better analytics
  if (path === '/') {
    data.page_category = 'home';
  } else if (path.startsWith('/services/')) {
    data.page_category = 'services';
    data.service_type = path.split('/')[2]; // Extract service type
  } else if (path.startsWith('/case-studies')) {
    data.page_category = 'case_studies';
    if (path !== '/case-studies') {
      data.case_study_id = path.split('/')[2]; // Extract case study ID
    }
  } else if (path.includes('demo')) {
    data.page_category = 'demo';
  } else if (path === '/about') {
    data.page_category = 'about';
  } else if (path === '/contact') {
    data.page_category = 'contact';
  }

  // Add search parameters if present
  if (location.search) {
    const searchParams = new URLSearchParams(location.search);
    data.url_parameters = Object.fromEntries(searchParams.entries());
  }

  return data;
};

export default useRouterTracking;